import React, { useContext, useEffect, useState } from 'react'
import { useLazyQuery, gql, NetworkStatus } from '@apollo/client'
import { Paper, Typography } from '@material-ui/core'
import moment from 'moment'
import ChartLine from '../../../../components/charts/ChartLine'
import { ChartLineThemes } from '../../../../components/charts/types/ChartLine'
import ChartLineSkeleton from '../../../../components/skeletons/ChartLineSkeleton'
import { DailyMonitoringFiltersContext } from '../../../../context/DailyMonitoringFiltersContext'
import { FilterDailyMonitoring } from '../../../../types/FilterDailyMonitoring'
import { formatDateCard } from '../../../../utils/Helpers'
import { STATUS_DELIVERED } from '../../../../types/Order'
import { defaultDateRange, defaultFormatTimeZone } from '../../../../utils/consts'

type BucketTypes = {
  key_as_string: string
  key: number,
  doc_count: number
  paymentMethods: {
    doc_count: number
  }
}

type Series = {
  name: string
  data: number[]
}

const PaymentsTypesHistory = (): JSX.Element => {
  const [dateInterval, setDateInterval] = useState('1d')
  const [calendarInterval, setCalendarInterval] = useState('calendar_interval')
  const METHOD_CREDIT_CARD = 'Cartão de crédito'
  const METHOD_APP = 'Pelo app'
  const METHOD_DELIVERED = 'Na entrega'

  const GET_DATA = gql`
    query SalesAverageHistory($filters: JSON) {
      ordersAggs(
        filters: $filters,
        size: 0,
        query: {
          bool: {
            must: [
              {
                match: {
                  status_translate_keyword: "${STATUS_DELIVERED}"
                }
              }
            ]
          }
        },
        aggs: {
          salesOverTime: {
            date_histogram: {
              field: "created_at",
              ${calendarInterval}: "${dateInterval}",
              time_zone: "-03:00"
            },
            aggs: {
              paymentMethods: {
                filter: {
                  term: {
                    payment_method_translate_keyword: "${METHOD_CREDIT_CARD}"
                  }
                }
              }
            }
          }
        }
      ) {
        aggregations
      }
    }
  `

  const { filters } = useContext(DailyMonitoringFiltersContext)
  const categories: Array<string> = []
  const series: Array<Series> = []
  series.push({ name: METHOD_APP, data: [] })
  series.push({ name: METHOD_DELIVERED, data: [] })

  const [getData, { loading, error, data, networkStatus }] = useLazyQuery(
    GET_DATA, {
      notifyOnNetworkStatusChange: true
    }
  )

  useEffect(() => {
    let createdAt: FilterDailyMonitoring['dateRange'] = { ...defaultDateRange }

    if (filters.dateRange) {
      createdAt = {
        ...filters.dateRange,
        ...defaultFormatTimeZone
      }
    }

    const a = moment(createdAt.initialDate)
    const b = moment(createdAt.finalDate)
    const difference = b.diff(a, 'day')

    if (difference >= 0 && difference <= 15) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1d')
    } else if (difference >= 16 && difference <= 91) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1w')
    } else if (difference >= 92 && difference <= 182) {
      setCalendarInterval('fixed_interval')
      setDateInterval('15d')
    } else if (difference >= 183 && difference <= 366) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1M')
    } else if (difference > 366) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1q')
    }

    getData({
      variables: { filters: { ...filters, createdAt } }
    })
  }, [filters])

  if (networkStatus === NetworkStatus.refetch) return <ChartLineSkeleton />
  if (loading || !data) return <ChartLineSkeleton />
  if (error) return <p>Error :(</p>
  if (!data.ordersAggs.aggregations) return <p>No data found</p>

  const { buckets } = data.ordersAggs.aggregations.salesOverTime

  series.map(serie => {
    buckets.map((bucket: BucketTypes) => {
      if (serie.name === METHOD_APP) {
        serie.data.push(bucket.paymentMethods.doc_count)
      } else {
        serie.data.push(bucket.doc_count - bucket.paymentMethods.doc_count)
      }
    })
  })

  buckets.map((bucket: BucketTypes) => {
    categories.push(bucket.key_as_string)
  })

  const options = {
    chart: {
      id: 'payment-types-history'
    },
    xaxis: {
      categories,
      type: 'datetime',
      labels: {
        formatter: (value: Date) => {
          return moment(value).format('DD-MM-YYYY')
        }
      }
    },
    yaxis: {
      categories
    }
  }

  return <Paper elevation={2}>
    <Typography paragraph style={{ paddingLeft: '20px', paddingTop: '20px' }}>
      Método de pagamento ({formatDateCard(filters.dateRange?.initialDate, filters.dateRange?.finalDate)})
      {dateInterval === '1d'
        ? (<Typography variant='caption'> filtrado por dia</Typography>)
        : dateInterval === '1w'
          ? (<Typography variant='caption'> filtrado por semana</Typography>)
          : dateInterval === '15d'
            ? (<Typography variant='caption'> filtrado por quinzena</Typography>)
            : dateInterval === '1M'
              ? (<Typography variant='caption'> filtrado por mês</Typography>)
              : dateInterval === '1q'
                ? (<Typography variant='caption'> filtrado por trimestre</Typography>)
                : ''
      }
    </Typography>
    <ChartLine options={options} series={series} theme={ChartLineThemes.rainbow} height={320} type="area"/>
  </Paper>
}

export default PaymentsTypesHistory
